import React, { useState, useRef } from 'react';
import { HeroImageProps } from './HeroImage.types';
import * as Styles from './HeroImage.styles';
import LazyImage from '../../helpers/LazyImage';
import sanitizeHtml from 'sanitize-html';
import CallToAction from '../../slices/CallToAction/CallToAction';
import CustomSearch from '../../components/CustomSearch/CustomSearch';

/**
 * Hero image banner with optional text and call to action with varying presentation of text area.
 * For optional use at the top of a service landing page, if set in the CMS.
 */
const HeroImage: React.FunctionComponent<HeroImageProps> = ({
  headline,
  content,
  callToActionText,
  callToActionURL,
  callToActionIsPrimary = true,
  backgroundBox = true,
  imageLarge,
  imageSmall,
  imageAltText,
  heroVideoUrl,
  breadcrumb,
  customSearch,
}) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  return (
    <>
      <LazyImage
        src={imageLarge}
        placeholder={imageSmall}
        visibilitySensorProps={{
          partialVisibility: true,
        }}
      >
        {(src) => (
          <Styles.Container $image={src} $backgroundBox={backgroundBox} data-testid="HeroImage">
            {heroVideoUrl && (
              <>
                <Styles.VideoContainer>
                  <Styles.VideoBackground ref={videoRef} autoPlay loop muted>
                    <source src={heroVideoUrl} type="video/mp4" />
                      Your browser doesn't support this video.
                  </Styles.VideoBackground>
                  <Styles.PlayPauseButton onClick={togglePlay}>
                    {isPlaying ? '⏸' : '▶'}
                  </Styles.PlayPauseButton>
                </Styles.VideoContainer>
              </>
            )}
            <Styles.InnerContainer>
              <Styles.Overlay $backgroundBox={backgroundBox} data-testid="HeroImageOverlay">
                {breadcrumb && (
                  <Styles.BreadcrumbLink href={breadcrumb.url} $backgroundBox={backgroundBox}>
                    {breadcrumb.title}
                  </Styles.BreadcrumbLink>
                )}
                {headline && <Styles.Headline level={1} text={headline} $backgroundBox={backgroundBox} />}
                {content && <Styles.Content dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />}
                {customSearch && (
                  <Styles.Search>
                    <CustomSearch {...customSearch} />
                  </Styles.Search>
                )}
                {callToActionURL && backgroundBox && (
                  <CallToAction url={callToActionURL} text={callToActionText} primary={callToActionIsPrimary} />
                )}
                {!callToActionURL && backgroundBox && <br />}
                {callToActionURL && !backgroundBox && (
                  <Styles.CallToActionLink href={callToActionURL}>
                    {callToActionText ? callToActionText : 'Find out more'}
                  </Styles.CallToActionLink>
                )}
              </Styles.Overlay>
            </Styles.InnerContainer>
          </Styles.Container>
        )}
      </LazyImage>
      {imageAltText && <span role="img" aria-label={imageAltText} />}
    </>
  );
};

export default HeroImage;
